import { useRef, useState } from 'react';
import { z } from 'zod';
import type { Citation, HistoryEntry } from '@/Public/ChatBot/Types';

const chatbotResponseSchema = z.object({
    chat_id: z.string().default(''),
    response: z.string().default(''),
    citations: z
        .array(
            z.object({
                source_document_link: z.string(),
                source_document_title: z.string().default(''),
            }),
        )
        .default([]),
    answerable: z.boolean().default(true),
    error: z.string().default(''),
});

export interface ChatbotApiResponse {
    readonly content: string;
    readonly citations: readonly Citation[];
    readonly answerable: boolean;
}

interface FetchChatbotResult {
    readonly chatId: string;
    readonly response: ChatbotApiResponse;
    readonly error?: string;
}

export interface SendMessageResult {
    readonly chatId: string;
    readonly response: ChatbotApiResponse;
}

interface UseChatbotApiReturn {
    readonly sendMessage: (
        content: string,
        sessionId: string,
        history: readonly HistoryEntry[],
    ) => Promise<SendMessageResult>;
    readonly sendFeedback: (chatId: string, rating: 'up' | 'down', comment: string) => Promise<void>;
    readonly isLoading: boolean;
}

export async function sendChatbotFeedback(chatId: string, rating: 'up' | 'down', comment: string): Promise<void> {
    await fetch('/api/chatbot/feedback', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ chat_id: chatId, rating, comment }),
    });
}

export async function fetchChatbot(
    chatId: string,
    query: string,
    sessionId: string,
    signal: AbortSignal,
    history: readonly HistoryEntry[] = [],
): Promise<FetchChatbotResult> {
    const res = await fetch('/api/chatbot', {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ chat_id: chatId, query, session_id: sessionId, history }),
        signal,
    });

    const json: unknown = await res.json().catch(() => null);
    const parsed = chatbotResponseSchema.safeParse(json);

    const body = parsed.success
        ? parsed.data
        : {
            chat_id: chatId,
            response: '',
            citations: [],
            answerable: false,
            error: 'An unexpected error occurred.',
        };

    const resolvedChatId = body.chat_id || chatId;

    return {
        chatId: resolvedChatId,
        response: {
            content: body.response,
            citations: body.citations,
            answerable: body.answerable,
        },
        error: !res.ok || body.error ? body.error || `Request failed (${res.status})` : undefined,
    };
}

export function useChatbotApi(): UseChatbotApiReturn {
    const [isLoading, setIsLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const sendMessage = async (
        content: string,
        sessionId: string,
        history: readonly HistoryEntry[],
    ): Promise<SendMessageResult> => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);

        try {
            const result = await fetchChatbot('', content, sessionId, controller.signal, history);
            if (result.error) {
                throw new Error(result.error);
            }
            return { chatId: result.chatId, response: result.response };
        } finally {
            setIsLoading(false);
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
        }
    };

    const sendFeedback = async (chatId: string, rating: 'up' | 'down', comment: string): Promise<void> => {
        if (!chatId) return;
        await sendChatbotFeedback(chatId, rating, comment);
    };

    return { sendMessage, sendFeedback, isLoading };
}
